import {Editable, useSelected, useSlate} from "slate-react"
import React, {useCallback, useMemo} from "react"
import { Editor} from "slate"
import isHotkey from "is-hotkey"
import {makeStyles} from "@material-ui/core/styles"
import * as PropTypes from "prop-types"
import RichTextEditor from "./RichTextEditor"
import {styled} from "@material-ui/core";
import i18n from "../../core/i18n";

const HOTKEYS = {
    'ctrl+b': 'bold',
    'ctrl+i': 'italic',
    'ctrl+u': 'underline',
    'ctrl+s': 'strikethrough',
    'ctrl+`': 'code',
}

const BulletListElement = React.forwardRef(({attributes, children, element}, ref) => {
    const style = { textAlign: element.align }
    return (
        <ul style={style} {...attributes}>
            {children}
        </ul>
    )
})

const NumberedListElement = React.forwardRef(({attributes, children, element}, ref) => {
    const style = { textAlign: element.align }
    return (
        <ol style={style} {...attributes}>
            {children}
        </ol>
    )
})

const ListItemElement = React.forwardRef(({attributes, children, element}, ref) => {
    const style = { textAlign: element.align }
    return (
        <li style={style} {...attributes}>
            {children}
        </li>
    )
})

// Define a React component renderer for our code blocks.
const CodeElement = React.forwardRef(({attributes, children}, ref) => {
    return (
        <code>{children}</code>
    )
})

const QuoteElement = React.forwardRef(({attributes, children, element}, ref) => {
    return (
        <blockquote {...attributes}>
            {children}
        </blockquote>
    )
})

const allowedSchemes = ['http:', 'https:', 'mailto:', 'tel:']
// Put this at the start and end of an inline component to work around this Chromium bug:
// https://bugs.chromium.org/p/chromium/issues/detail?id=1249405

const InlineChromiumBugfixSpan = styled('span')({
    fontSize: 0,
});
const InlineChromiumBugfix = () => (
    <InlineChromiumBugfixSpan contentEditable={false}>
        {String.fromCodePoint(160) /* Non-breaking space */}
    </InlineChromiumBugfixSpan>
);

const StyledLink = styled('a', {
    shouldForwardProp: (prop) => prop !== 'selected',
})(({ selected }) => ({
    boxShadow: selected ? '0 0 0 3px #ddd' : 'none',
}));

const LinkElement = React.forwardRef(({ attributes, children, element }, ref) => {
    const selected = useSelected();
    const safeUrl = useMemo(() => {
        let parsedUrl = null;
        try {
            parsedUrl = new URL(element.url);
        } catch {}
        if (parsedUrl && allowedSchemes.includes(parsedUrl.protocol)) {
            return parsedUrl.href;
        }
        return 'about:blank';
    }, [element.url]);

    let title;
    if(element.title) {
        title = element.title;
    } else {
        title = safeUrl;
        // 修正指定 title 功能之前的超連結。
        if(React.Children.count(children) === 1) {
            const child = React.Children.toArray(children)[0];
            if(child.props?.text?.text) {
                title = child.props.text.text;
            }
        }
    }

    return (
        <StyledLink {...attributes} title={title} href={safeUrl} selected={selected} ref={ref}>
            <InlineChromiumBugfix />
            {children}
            <InlineChromiumBugfix />
        </StyledLink>
    );
});

const DefaultElement = React.forwardRef(({attributes, children, element}, ref) => {
    const style = { textAlign: element.align }
    return <p style={style} {...attributes}>{children}</p>
})

// Define a React component to render leaves with bold text.
const Leaf = React.forwardRef( ({ attributes, children, leaf }, ref) => {
    const style = {}

    if (leaf.bold) {
        children = <b>{children}</b>
    }

    if (leaf.code) {
        children = <code>{children}</code>
    }

    if (leaf.italic) {
        children = <i>{children}</i>
    }

    if (leaf.underline) {
        children = <u>{children}</u>
    }

    if (leaf.strikethrough) {
        children = <s>{children}</s>
    }

    if (leaf.superscript) {
        children = <sup>{children}</sup>
    }

    if (leaf.subscript) {
        children = <sub>{children}</sub>
    }

    if(leaf.size) {
        style.fontSize = leaf.size
    }

    if(leaf.color) {
        style.color = leaf.color
    }

    if(leaf.background) {
        style.background = leaf.background
    }

    return <span {...attributes} style={style}>{children}</span>
})

const useStyles = makeStyles(theme => ({
    root: {
        '& p': {
            margin: '0.5em'
        },
        '& ol,ul': {
            margin: '0.5em',
            paddingInlineStart: '2em'
        },
        '& blockquote': {
            borderLeft: 'solid 3px lightgray',
            padding: '0 0.5em',
            margin: '0 0.5em'
        },
        '& code': {
            display: 'block', /* Ensures <code> spans the full width */
            whiteSpace: 'pre-wrap', /* Enables line breaks */
            overflowWrap: 'break-word', /* Prevents overflow by breaking long words */
            fontFamily: 'monospace', /* Monospaced font for code */
            border: 'solid 1px lightgray',
            backgroundColor: '#f5f5f5', /* Light gray background for contrast */
            borderRadius: '4px',
            padding: '0.5em',
            margin: '0 0.5em'
        },
        '& span>code': {
            display: 'inline',
            padding: '1px',
        }
    }
}))

function RichTextCanvas(props) {
    const {placeholder, autofocus} = props
    const classes = useStyles()
    const editor = useSlate()
    const { isInline } = editor

    editor.isInline = element => {
        return element.type === 'link' ? true : isInline(element)
    }

    // Define a rendering function based on the element passed to `props`. We use
    // `useCallback` here to memoize the function for subsequent renders.
    const renderElement = useCallback(props => {
        switch (props.element.type) {
            case 'bulleted-list':
                return <BulletListElement {...props} />
            case 'numbered-list':
                return <NumberedListElement {...props} />
            case 'list-item':
                return <ListItemElement {...props} />
            case 'code':
                return <CodeElement {...props} />
            case 'quote':
                return <QuoteElement {...props} />
            case 'link':
                return <LinkElement {...props} />
            default:
                return <DefaultElement {...props} />
        }
    }, [])

    // Define a leaf rendering function that is memoized with `useCallback`.
    const renderLeaf = useCallback(props => {
        return <Leaf {...props} />
    }, [])

    const handleKeyDown = useCallback(event => {
        if (event.key === 'Enter' && !event.shiftKey
            && RichTextEditor.isPreFormattedActive(editor)) {
            event.preventDefault()
            // Insert a line break instead of creating a new block
            Editor.insertText(editor, '\n')
            return
        }

        if (!event.ctrlKey) {
            return
        }

        for (const hotkey in HOTKEYS) {
            if (isHotkey(hotkey, event)) {
                event.preventDefault()
                const mark = HOTKEYS[hotkey]
                RichTextEditor.toggleMark(editor, mark)
            }
        }
    }, [editor])

    const readOnly = props.readOnly || props.disabled

    return (
        <Editable className={classes.root}
            // Add the editable component inside the context.
            // Pass in the `renderElement` function.
                  renderElement={renderElement}
            // Pass in the `renderLeaf` function.
                  renderLeaf={renderLeaf}
                  onKeyDown={handleKeyDown}
                  style={{outlineStyle: 'none'}}
                  readOnly={readOnly} placeholder={i18n.translate(placeholder)}
                  spellCheck autoFocus={autofocus}
        />
    )
}

RichTextCanvas.propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
    readOnly: PropTypes.bool,
    disabled: PropTypes.bool,
    placeholder: PropTypes.string,
    helperText: PropTypes.string,
    autofocus: PropTypes.bool
}

export default RichTextCanvas